// Phase 18 Plan 02 Task 1 — POLISH-06 hand-rolled focus trap.
//
// Single React hook handling all four modal-focus concerns:
//   1. Initial focus on open — first interactive element, else modal root.
//   2. Tab + Shift+Tab trap inside the modal (wrap last <-> first).
//   3. Escape — invoke onClose.
//   4. Restore previously-focused element on close.
//
// DEC-015 — hand-rolled per CONTEXT.md Area 1 Q2 (no focus-trap / focus-trap-react / react-focus-lock).
// Pattern: W3C ARIA APG Dialog (Modal) — https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/
// Re-queries focusables on every Tab because modal contents change after open
// (form validation reveals error links, async data renders new buttons, dropdowns expand).
// RESEARCH Pattern 1 §"Why re-query on every Tab" — this is the #1 bug in naive implementations.

import { type RefObject, useEffect } from 'react'

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), ' +
  'input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  isOpen: boolean,
  onClose: () => void,
): void {
  useEffect(() => {
    if (!isOpen || !ref.current) return

    const previouslyFocused = document.activeElement as HTMLElement | null
    const modal = ref.current

    // 1. Initial focus
    const focusables = modal.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    const firstFocusable = focusables[0]
    if (firstFocusable) {
      firstFocusable.focus()
    } else {
      // No interactive children — focus the modal itself (caller must set tabIndex={-1}).
      modal.focus()
    }

    function onKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }
      if (e.key !== 'Tab') return

      // Re-query — modal contents may have mutated since open.
      const items = modal.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      const first = items[0]
      const last = items[items.length - 1]
      if (!first || !last) {
        e.preventDefault()
        return
      }
      const active = document.activeElement as HTMLElement | null

      if (e.shiftKey && active === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && active === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      // Restore previous focus on close — null-safe; happy-dom returns null for body.activeElement edge cases.
      previouslyFocused?.focus()
    }
  }, [isOpen, onClose, ref])
}
